import { useMemo, useState } from "react";
import { FaCalendarAlt, FaChevronRight } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useGetEventsQuery } from "../../features/api/appApi";
import julyContestBanner from "../../images/july-memorial-programming-contest-2026.png";

const EventsSection = () => {
  const { data: events = [] } = useGetEventsQuery();
  const [activeTab, setActiveTab] = useState("upcoming");

  const grouped = useMemo(
    () => ({
      upcoming: events.filter((e) => e.status === "upcoming"),
      past: events.filter((e) => e.status === "past"),
    }),
    [events]
  );

  const visibleEvents = grouped[activeTab] || [];

  return (
    <section className="home-section pt-8 sm:pt-10">
      <div className="home-container">
        {/* Featured / Ongoing Event Banner */}
        <div className="mb-12">
          <Link
            // to="/events/july-memorial-programming-contest-2026"
            to="#"
            onClick={(e) => {
              e.preventDefault();
              alert("Registration is closed!");
            }}
            className="no-underline block group"
          >
            <div className="home-card-strong overflow-hidden transition-all duration-300 hover:border-[var(--brand)] hover:shadow-lg">
              <div className="grid gap-6 md:grid-cols-[1.2fr_0.8fr] lg:grid-cols-[1.4fr_0.6fr]">
                {/* Text Content */}
                <div className="flex flex-col justify-between p-6 sm:p-8 lg:p-10">
                  <div>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-red-600 dark:bg-red-500/20 dark:text-red-400">
                      <span className="h-2 w-2 rounded-full bg-red-500 " />
                      Registration Closed
                    </span>
                    <h3 className="mt-4 text-2xl font-bold tracking-tight text-[var(--text)] sm:text-3xl lg:text-4xl group-hover:text-[var(--brand)] transition-colors">
                      July Memorial Programming Contest 2026
                    </h3>
                    <p className="mt-3 text-sm sm:text-base home-muted leading-relaxed">
                      Participate in the special memorial edition contest honoring the spirit of the July Uprising. Challenge your algorithmic and problem-solving skills in teams of two.
                    </p>
                  </div>
                  
                  <div className="mt-6 flex flex-wrap items-center gap-4">
                    <span className="home-btn-primary px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
                      Registration Closed
                      <FaChevronRight className="text-[10px]" />
                    </span>
                    <span className="text-xs sm:text-sm font-semibold home-muted group-hover:opacity-80 transition-opacity">
                      View Event Page
                    </span>
                  </div>
                </div>

                {/* Banner Image */}
                <div className="relative min-h-[220px] md:min-h-full overflow-hidden">
                  <img
                    src={julyContestBanner}
                    alt="July Memorial Programming Contest 2026"
                    className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--surface-strong)]/20 via-transparent to-transparent" />
                </div>
              </div>
            </div>
          </Link>
        </div>

        <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
          <div>
            <span className="section-kicker">Events</span>
            <h2 className="home-title mt-4 text-2xl font-bold sm:text-4xl">All community events in one place.</h2>
          </div>

          <div className="home-card inline-flex p-1">
            <button
              onClick={() => setActiveTab("upcoming")}
              className={`rounded-lg px-4 py-2 text-sm font-semibold ${activeTab === "upcoming" ? "home-btn-primary" : "home-muted"}`}
            >
              Upcoming
            </button>
            <button
              onClick={() => setActiveTab("past")}
              className={`rounded-lg px-4 py-2 text-sm font-semibold ${activeTab === "past" ? "home-btn-primary" : "home-muted"}`}
            >
              Past
            </button>
          </div>
        </div>

        {visibleEvents.length === 0 ? (
          <div className="home-card p-10 text-center">
            <FaCalendarAlt className="mx-auto text-2xl home-muted" />
            <p className="mt-3 text-sm home-muted">No {activeTab} events available right now.</p>
          </div>
        ) : (
          <div className="grid home-stagger-grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {visibleEvents.map((event) => {
              const CardContent = (
                <article className="home-card group overflow-hidden p-5 h-full cursor-pointer">
                  <div className="mb-4 overflow-hidden rounded-xl">
                    <img
                      src={
                        event.bgImageLink ||
                        "https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1200&q=80"
                      }
                      alt={event.title}
                      className={`h-40 w-full object-cover transition-transform duration-300 group-hover:scale-105 ${
                        event.isJulyContestLink ? "object-top" : ""
                      }`}
                    />
                  </div>
                  <div className="mb-4 flex items-center justify-between gap-2">
                    <span className="home-pill px-2.5 py-1 text-xs font-semibold uppercase">{event.type || "event"}</span>
                    <span className="text-xs uppercase home-muted">{event.status}</span>
                  </div>

                  <h3 className="text-lg font-semibold">{event.title}</h3>

                  <div className="mt-4 flex items-center justify-between">
                    <p className="text-sm home-muted">
                      {new Date(event.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold home-muted group-hover:opacity-70">
                      View <FaChevronRight className="text-[10px]" />
                    </span>
                  </div>
                </article>
              );

              if (event.isJulyContestLink) {
                return (
                  <Link
                    key={`${activeTab}-${event.id}`}
                    to="/events/july-memorial-programming-contest-2026"
                    className="no-underline block"
                  >
                    {CardContent}
                  </Link>
                );
              }

              return <div key={`${activeTab}-${event.id}`}>{CardContent}</div>;
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default EventsSection;